import type { SchemaRegion } from "../../../api/restaurantSchema";

interface RegionCell {
  id: number | null;
  name: string;
  caption: string | null;
  marked: boolean;
}

/**
 * Регионите на телефон: решетка по 3 на ред, ВСИЧКИ видими — клетки като
 * плочките за ден/час във формата (радиус 10, кехлибарена избрана). Отгоре
 * името (до 2 реда — не се реже), отдолу надпис (`captionOf`, напр. „24 свободни"; null — без), а
 * кехлибарена точка горе вдясно отбелязва регион с нещо важно (`markOf`,
 * напр. избрана маса). `all` — първа клетка „Всички" (activeId null).
 */
const RegionGrid = ({
  regions,
  activeId,
  onChange,
  captionOf,
  markOf,
  markLabel,
  all,
}: {
  regions: SchemaRegion[];
  activeId: number | null;
  onChange: (regionId: number | null) => void;
  captionOf: (region: SchemaRegion) => string | null;
  markOf?: (region: SchemaRegion) => boolean;
  // Текстът за екранния четец на точката.
  markLabel?: string;
  all?: { label: string; caption: string | null };
}) => {
  const cells: RegionCell[] = [
    ...(all ? [{ id: null, name: all.label, caption: all.caption, marked: false }] : []),
    ...regions.map((region) => ({
      id: region.id,
      name: region.name,
      caption: captionOf(region),
      marked: markOf?.(region) ?? false,
    })),
  ];

  return (
    <div role="tablist" className="grid grid-cols-3 gap-1.5">
      {cells.map(({ id, name, caption, marked }) => {
        const active = id === activeId;
        return (
          <button
            key={id ?? "all"}
            type="button"
            role="tab"
            aria-selected={active}
            title={name}
            onClick={() => onChange(id)}
            className={`relative flex min-h-[52px] min-w-0 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[10px] border px-1.5 py-1.5 transition-colors duration-150 ${
              active ? "border-brand bg-brand-tint" : "border-shade-3 bg-surface hover:bg-fill"
            }`}
          >
            <span
              className={`line-clamp-2 max-w-full break-words text-center text-[13px] font-bold leading-[1.2] ${
                active ? "text-brand-deep-ink" : "text-ink"
              }`}
            >
              {name}
            </span>
            {caption !== null && (
              <span
                className={`max-w-full truncate text-[11.5px] font-semibold leading-[1.2] tabular-nums ${
                  active ? "text-brand-deep-ink" : "text-muted"
                }`}
              >
                {caption}
              </span>
            )}
            {!active && marked && (
              <span aria-label={markLabel} className="absolute right-1.5 top-1.5 size-2 rounded-full bg-brand" />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default RegionGrid;
